/**
 * src/components/SaveDocumentDialog.tsx — "Save Document" trigger + dialog + overwrite prompt.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/SaveDocumentDialog.tsx` (whole file, 259
 * lines) @ `8d01f0c`.
 * Spec: `docs/spec/06-frontend-components.md` §8.1 (every field, string and branch), §7.1 (what is
 * actually persisted), §7.3 F9/F10/F11, §15 D-2/D-8/D-12, §16 ranks 2 and 16.
 *
 * Rendered by `ConverterApp` in the toolbar row (`ConverterApp.tsx:256-259`), alongside
 * {@link SavePreambleDialog}. Both trigger buttons are always mounted; the `DialogContent` and the
 * overwrite `AlertDialog` are portalled only while open.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * DF-22 rank 2 — THE UPDATE PATH IS UNREACHABLE, AND SAVING A LOADED DOCUMENT DUPLICATES IT
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `documentId`, `initialTitle`, `initialPreambleId` and `initialFolderId` are declared here and
 * **never passed by anything**. `ConverterApp.tsx:256-259` renders exactly:
 *
 *     <SaveDocumentDialog latexContent={docContent} htmlContent={html} />
 *
 * and `handleLoadDocument` (`ConverterApp.tsx:116-122`) discards the `documentId` the sidebar hands
 * it. So `documentId` is always `undefined`, the first branch of {@link performSave} is dead code,
 * and loading a saved document, editing it and saving again **creates a second document** — or, if
 * the user retypes the identical title into the identical folder, walks them through the
 * "Document Already Exists / Replace" prompt every single time (spec §7.3 F9).
 *
 * **This component must NOT reach for the loaded id itself.** Threading `loadedDocumentId` in is the
 * proposed fix, it belongs to `ConverterApp`, and it is deferred pending visual validation
 * (CLAUDE.md §3, DEFERRED-FIXES.md DF-22 rank 2). The unreachable branch is ported verbatim rather
 * than deleted, because deleting it would quietly decide the product question.
 *
 * ═══ DF-20 — `html_content` IS GONE FROM THE WIRE ═════════════════════════════════════════════
 * Owner-approved 2026-07-29. The source sent `html_content: htmlContent` in all three mutation
 * payloads (`SaveDocumentDialog.tsx:97`, `:117`, `:138`); the column no longer exists
 * (`migrations/0001_init.sql`) and {@link CreateDocumentInput}/{@link UpdateDocumentInput} have no
 * such field, so the key is omitted from every body below.
 *
 * The `htmlContent` **prop stays** — it is part of the component contract
 * (`SaveDocumentDialog.tsx:35-42`) that `ConverterApp` satisfies, and `src/lib/types.ts` blesses
 * keeping it. It is deliberately not destructured: nothing may read it. Note the consequence for
 * the dialog copy below — the description still says "and HTML conversion to the database", which
 * is now literally untrue. That string is reproduced byte-for-byte anyway (bug-for-bug); see the
 * report accompanying this port.
 *
 * ═══ OTHER DEFECTS DELIBERATELY REPRODUCED HERE ═══════════════════════════════════════════════
 *  - **§15 D-8 (absent from the DF-22 rank table — see the port report).** The duplicate-title check
 *    is gated on `&& documentsInFolder` (`:73`). Changing the folder Select swaps the React Query
 *    key, `data` goes `undefined` while the new folder loads, and a save issued in that window
 *    skips the overwrite prompt entirely and creates a silent same-title duplicate. Do NOT disable
 *    Save while `useDocumentsByFolder` is fetching.
 *  - **DF-22 rank 16.** The preamble dropdown appends `' (Default)'` when `is_default` is set
 *    (`:216`) and that badge, plus one in the sidebar, is the *only* thing in the app that reads
 *    the flag. Nothing auto-applies a default preamble. Do not add it here.
 *  - **DF-22 rank 5.** Both mutations' failures surface only through `use-database.ts`'s generic
 *    destructive toast; this dialog renders no error state and stays open on failure, because
 *    `setOpen(false)` lives in `onSuccess` only.
 *  - **DF-22 rank 9 (the one approved deviation), second half.** A successful create fires TWO
 *    toasts: `'Document saved'` from `useCreateDocument` and `'Document saved successfully'` from
 *    `:148` below. Both are kept; they now stack in sonner instead of appearing in two widgets.
 *  - **Dialog overflow.** `sm:max-w-[425px] max-h-[90vh] overflow-y-auto` (`:164`) is the fix
 *    recorded in the source's `ISSUES_AND_FIXES.md:15-21`. Reproduced exactly — the dialog scrolls
 *    internally rather than growing past the viewport when the document is long.
 *  - **No Enter-to-submit.** The folder dialogs have it; the save dialogs deliberately do not
 *    (spec §14 C-8). No keyboard shortcut of any kind (DF-22 rank 15).
 */
import { useState } from 'react'
import {
  useCreateDocument,
  useDocumentsByFolder,
  useFolders,
  usePreambles,
  useUpdateDocument,
} from '@/hooks/use-database'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Save } from 'lucide-react'
import { toast } from 'sonner'

/**
 * `SaveDocumentDialog.tsx:35-42`, field for field.
 *
 * Everything below `htmlContent` is dead in the shipped app — DF-22 rank 2. See the file header.
 */
export interface SaveDocumentDialogProps {
  /**
   * **DF-22 rank 1 (owner-approved 2026-08-09).** Called after any successful save — create,
   * update or overwrite — so `ConverterApp` can clear its unsaved-changes flag.
   *
   * Purely additive: it reports, it does not decide. Every existing `onSuccess` still closes the
   * dialog, resets the same fields and raises the same toast, in the same order. Without it the
   * `beforeunload` warning would fire *after* a successful save, and a warning that cries wolf is
   * worse than none — people learn to click through it.
   */
  onSaved?: () => void
  /** The **document tab only**. The preamble is saved separately and is not part of this row (spec §7.1). */
  latexContent: string
  /**
   * DF-20: accepted, never read, never sent. Kept because it is part of the ported contract and
   * `ConverterApp.tsx:257-258` passes it. Do not thread it into a mutation body.
   */
  htmlContent: string
  /** DF-22 rank 2 — never passed. Its presence is what would enable the update branch. */
  documentId?: number
  /** DF-22 rank 2 — never passed. Read once, at mount, into `title`. */
  initialTitle?: string
  /** DF-22 rank 2 — never passed. */
  initialPreambleId?: number
  /** DF-22 rank 2 — never passed. */
  initialFolderId?: number
}

export const SaveDocumentDialog = ({
  onSaved,
  latexContent,
  // `htmlContent` is intentionally NOT destructured — DF-20, see the file header.
  documentId,
  initialTitle = '',
  initialPreambleId,
  initialFolderId,
}: SaveDocumentDialogProps) => {
  // `SaveDocumentDialog.tsx:52-57`. `initialTitle`/`initialPreambleId`/`initialFolderId` are read
  // once at mount and never resynced — a prop change after mount would not reach these. Moot while
  // nothing passes them (DF-22 rank 2); reproduced as written.
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(initialTitle)
  const [preambleId, setPreambleId] = useState<number | undefined>(initialPreambleId)
  const [folderId, setFolderId] = useState<number | undefined>(initialFolderId)
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false)
  const [existingDocId, setExistingDocId] = useState<number | null>(null)

  // `:59-63`. All four queries run whenever this component is mounted — i.e. from first paint of
  // `ConverterApp`, long before the dialog is opened. `folderId || null` sends `null` for "Root".
  const { data: preambles } = usePreambles()
  const { data: folders } = useFolders()
  const { data: documentsInFolder } = useDocumentsByFolder(folderId || null)
  const { mutate: createDocument, isPending: isCreating } = useCreateDocument()
  const { mutate: updateDocument, isPending: isUpdating } = useUpdateDocument()

  /** `:65-86`. */
  const handleSave = () => {
    if (!title.trim()) {
      toast.error('Please enter a title')
      return
    }

    // Check for duplicate name in the same folder (only when creating new or changing name)
    // Only check if we have loaded the documents for the current folder
    //
    // ⚠️ §15 D-8 — `&& documentsInFolder` is the race. While the by-folder query is in flight the
    // data is `undefined`, the whole check is skipped, and the save creates a silent duplicate.
    // Reproduced deliberately; do not gate the Save button on `isFetching`.
    if ((!documentId || title !== initialTitle) && documentsInFolder) {
      const duplicate = documentsInFolder.find(
        (doc) => doc.title.toLowerCase() === title.toLowerCase() && doc.id !== documentId,
      )

      if (duplicate) {
        setExistingDocId(duplicate.id)
        setShowOverwriteDialog(true)
        return
      }
    }

    performSave(false)
  }

  /**
   * `:88-153` — three branches.
   *
   * Branch 1 (`documentId` set) is **unreachable** (DF-22 rank 2) and is the only one that does not
   * reset the form on success (`:103-106`). Note it also ignores `existingDocId` entirely: reached
   * via Replace it would update the *loaded* document under the colliding title and still toast
   * `'Document replaced successfully'`, leaving the real duplicate untouched. Ported as written.
   */
  const performSave = (isOverwrite: boolean) => {
    if (documentId) {
      // Update existing document
      updateDocument(
        {
          id: documentId,
          updates: {
            title,
            latex_content: latexContent,
            // DF-20: `html_content: htmlContent` was here (`:97`). The column is gone.
            preamble_id: preambleId,
            folder_id: folderId,
          },
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSaved?.()
            toast.success(
              isOverwrite ? 'Document replaced successfully' : 'Document updated successfully',
            )
          },
        },
      )
    } else if (isOverwrite && existingDocId) {
      // Overwrite existing document
      updateDocument(
        {
          id: existingDocId,
          updates: {
            title,
            latex_content: latexContent,
            // DF-20: `html_content: htmlContent` was here (`:117`).
            preamble_id: preambleId,
            folder_id: folderId,
          },
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSaved?.()
            setTitle('')
            setPreambleId(undefined)
            setFolderId(undefined)
            toast.success('Document replaced successfully')
          },
        },
      )
    } else {
      // Create new document
      createDocument(
        {
          title,
          latex_content: latexContent,
          // DF-20: `html_content: htmlContent` was here (`:138`).
          preamble_id: preambleId,
          folder_id: folderId,
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSaved?.()
            setTitle('')
            setPreambleId(undefined)
            setFolderId(undefined)
            toast.success('Document saved successfully')
          },
        },
      )
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {/* `:158-163`. `documentId` is always undefined ⇒ the label is always 'Save Document'. */}
          <Button variant="default" className="gap-2">
            <Save className="h-4 w-4" />
            {documentId ? 'Update Document' : 'Save Document'}
          </Button>
        </DialogTrigger>
        {/* `:164` — `max-h-[90vh] overflow-y-auto` is the source's own overflow fix. Keep it. */}
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{documentId ? 'Update Document' : 'Save Document'}</DialogTitle>
            <DialogDescription>
              {/*
                DF-20 note: "and HTML conversion to the database" is no longer true — only LaTeX is
                stored now. The string is reproduced verbatim (`:170`) because rewriting user-facing
                copy is a fix, and fixes are deferred. Flagged in the port report.
              */}
              {documentId
                ? 'Update your document with the latest changes.'
                : 'Save your LaTeX document and HTML conversion to the database.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Document Title</Label>
              <Input
                id="title"
                placeholder="My LaTeX Document"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              {/* `:184-200`. A FLAT list — the folder hierarchy that exists in `FolderTree` is not
                  offered here (spec §10.3, `FOLDER_NAVIGATION_GUIDE.md:230`). Reproduced. */}
              <Label htmlFor="folder">Folder (Optional)</Label>
              <Select
                value={folderId?.toString() || 'none'}
                onValueChange={(value) => setFolderId(value === 'none' ? undefined : parseInt(value))}
              >
                <SelectTrigger id="folder">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Root (No folder)</SelectItem>
                  {folders?.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id.toString()}>
                      📁 {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              {/* `:203-220`. `preamble_id` is a LABEL ONLY: nothing on load reads it back and the
                  preamble tab is never repopulated from it (spec §7.1, §7.4 step 4). */}
              <Label htmlFor="preamble">Preamble (Optional)</Label>
              <Select
                value={preambleId?.toString() || 'none'}
                onValueChange={(value) =>
                  setPreambleId(value === 'none' ? undefined : parseInt(value))
                }
              >
                <SelectTrigger id="preamble">
                  <SelectValue placeholder="Select a preamble" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {preambles?.map((preamble) => (
                    <SelectItem key={preamble.id} value={preamble.id.toString()}>
                      {preamble.name}
                      {/* DF-22 rank 16 — one of only two places `is_default` is ever read. */}
                      {preamble.is_default && ' (Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? 'Saving...' : documentId ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* `:234-255`. The prompt every re-save of a loaded document walks through — DF-22 rank 2. */}
      <AlertDialog open={showOverwriteDialog} onOpenChange={setShowOverwriteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Document Already Exists</AlertDialogTitle>
            <AlertDialogDescription>
              A document with the name "{title}" already exists in this folder. Do you want to
              replace it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowOverwriteDialog(false)
                performSave(true)
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
